import { formatDuration } from './format';
/**
 * As noites de sono como o app Saude da Apple as desenha (B-41 e B-39, decididos em 14 e
 * 16/10/2026; os prints de referencia estao em `demandas/2026-10-14/`).
 *
 * Uma barra por noite, do DEITAR ao ACORDAR, com o eixo em hora do relogio e a barra
 * listrada pelas fases. A hora vira "minutos desde o meio-dia": a noite atravessa a
 * meia-noite, e contar a partir do meio-dia poe 22h e 06h no mesmo eixo, em ordem.
 */
/** As fases como o banco as grava (`measurement_value_detail.value`). */
export const SLEEP_STAGES = { AWAKE: 0, REM: 1, LIGHT: 2, DEEP: 3 };
const MINUTES_PER_DAY = 1440;
const NOON = 720;
/** Minutos desde o meio-dia do dia `day` (yyyy-mm-dd), para um instante. */
function sinceNoon(instant, day) {
    const noon = new Date(`${day}T12:00:00`).getTime();
    return (instant - noon) / 60_000;
}
function clock(instant) {
    const when = new Date(instant);
    return `${String(when.getHours()).padStart(2, '0')}:${String(when.getMinutes()).padStart(2, '0')}`;
}
/**
 * O instante de uma data do servidor. Sem fuso, como o resto do painel a le; a forma so
 * data ganha `T00:00:00` para nao voltar um dia.
 */
function instantOf(date) {
    if (!date) {
        return Number.NaN;
    }
    return new Date(date.includes('T') ? date : `${date}T00:00:00`).getTime();
}
/**
 * As barras das noites. A noite e do dia em que a pessoa DEITOU, como no relatorio de
 * sono; quem deita depois da meia-noite tem a noite do dia anterior.
 */
export function buildNightBars(nights) {
    const bars = nights
        .filter((night) => Number.isFinite(instantOf(night.date)) && Number.isFinite(instantOf(night.endDate)))
        .map((night) => {
        const start = instantOf(night.date);
        const end = instantOf(night.endDate);
        const startDate = new Date(start);
        // Deitar de madrugada (antes do meio-dia) e a noite do dia ANTERIOR.
        if (startDate.getHours() < 12) {
            startDate.setDate(startDate.getDate() - 1);
        }
        const day = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}`;
        const segments = (night.details ?? [])
            .map((detail) => ({
            from: sinceNoon(instantOf(detail.date), day),
            to: sinceNoon(instantOf(detail.endDate), day),
            stage: detail.value
        }))
            .filter((segment) => segment.to > segment.from);
        const asleep = (night.details ?? [])
            .filter((detail) => detail.value >= SLEEP_STAGES.REM && detail.value <= SLEEP_STAGES.DEEP)
            .reduce((sum, detail) => sum + (detail.duration ?? 0), 0);
        const byStage = (stage) => (night.details ?? []).filter((d) => d.value === stage).reduce((sum, d) => sum + (d.duration ?? 0), 0);
        const inBedSeconds = Math.max(0, Math.round((end - start) / 1000));
        const hasStages = (night.details ?? []).length > 0;
        const details = [
            `Deitou ${clock(start)} · acordou ${clock(end)}`,
            ...(hasStages ? [`Dormindo ${formatDuration(asleep)}`] : []),
            `Na cama ${formatDuration(inBedSeconds)}`,
            ...(hasStages
                ? [
                    `REM ${formatDuration(byStage(SLEEP_STAGES.REM))}`,
                    `Leve ${formatDuration(byStage(SLEEP_STAGES.LIGHT))}`,
                    `Profundo ${formatDuration(byStage(SLEEP_STAGES.DEEP))}`
                ]
                : ['Sem o detalhe das fases nesta noite'])
        ];
        return {
            day,
            label: `${day.slice(8, 10)}/${day.slice(5, 7)}`,
            from: sinceNoon(start, day),
            to: sinceNoon(end, day),
            segments,
            // Sem fases nao se estima o tempo dormindo (decidido em 16/10/2026).
            asleepSeconds: hasStages ? asleep : undefined,
            inBedSeconds,
            details
        };
    })
        .filter((bar) => bar.to > bar.from);
    // UMA noite por dia: a mais longa, como o backend e o relatorio de sono decidem
    // (V20260925_2). Um cochilo a tarde nao e a noite, e puxaria a media para baixo.
    const byDay = new Map();
    for (const bar of bars) {
        const current = byDay.get(bar.day);
        if (!current || bar.inBedSeconds > current.inBedSeconds) {
            byDay.set(bar.day, bar);
        }
    }
    return [...byDay.values()].sort((left, right) => left.day.localeCompare(right.day));
}
/** As medias do periodo, para o resumo acima das barras. */
export function nightAverages(bars) {
    const withStages = bars.filter((bar) => bar.asleepSeconds !== undefined);
    const average = (values) => values.length ? values.reduce((a, b) => a + b, 0) / values.length : undefined;
    return {
        asleepSeconds: average(withStages.map((bar) => bar.asleepSeconds)),
        inBedSeconds: average(bars.map((bar) => bar.inBedSeconds))
    };
}
/** A hora do relogio de "minutos desde o meio-dia": `22h`, `00h`, `06h`. */
export function clockLabelSinceNoon(minutes) {
    const total = (((NOON + Math.round(minutes)) % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
    return `${String(Math.floor(total / 60)).padStart(2, '0')}h`;
}
//# sourceMappingURL=sleep-nights.js.map